import React, { useEffect, useState } from 'react';
import { Form, Button, Row, Col, Spinner, Image, Card } from 'react-bootstrap';
import { FaTrash, FaSave, FaTimes, FaPlus } from 'react-icons/fa';
import { addNeighborhoodDevelopment, updateNeighborhoodDevelopment } from '../../services/neighborhoodService';

const toBase64 = (file) =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = () => resolve(reader.result);
    reader.onerror = (error) => reject(error);
  });

const createLocalId = () => `category-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;

const createCategoryState = (overrides = {}) => ({
  localId: overrides.localId || createLocalId(),
  guid: overrides.guid || null,
  name: overrides.name || '',
  description: overrides.description || '',
  existingImages: overrides.existingImages || [],
  newGalleryImages: overrides.newGalleryImages || [],
  newGalleryPreviews: overrides.newGalleryPreviews || [],
});

const getImageUrl = (url) => {
  if (!url) return null;
  if (url.startsWith('http') || url.startsWith('blob:')) return url;
  if (url.startsWith('/assets/')) return `https://iskenderun.bel.tr${url}`;
  if (url.startsWith('/uploads/')) {
    const apiUrl = process.env.REACT_APP_API_URL || 'https://api.iskenderun.bel.tr/api';
    const apiDomain = apiUrl.endsWith('/api') ? apiUrl.slice(0, -4) : apiUrl;
    return `${apiDomain}${url}`;
  }
  return `https://iskenderun.bel.tr${url.startsWith('/') ? url : `/${url}`}`;
};

const replaceTurkishChars = (value) =>
  value
    .replace(/İ/g, 'I')
    .replace(/I/g, 'I')
    .replace(/ı/g, 'i')
    .replace(/Ğ/g, 'G')
    .replace(/ğ/g, 'g')
    .replace(/Ü/g, 'U')
    .replace(/ü/g, 'u')
    .replace(/Ş/g, 'S')
    .replace(/ş/g, 's')
    .replace(/Ö/g, 'O')
    .replace(/ö/g, 'o')
    .replace(/Ç/g, 'C')
    .replace(/ç/g, 'c');

const generateSlug = (text) =>
  replaceTurkishChars(text || '')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[^-a-zA-Z0-9\s]+/g, '')
    .trim()
    .replace(/\s+/g, '-')
    .replace(/-+/g, '-')
    .toLowerCase();

const NeighborhoodForm = ({ editingItem, onSubmit, onCancel }) => {
  const [formData, setFormData] = useState({
    name: '',
    urlTag: '',
    title: '',
    description: ''
  });

  const [coverImage, setCoverImage] = useState(null);
  const [coverImagePreview, setCoverImagePreview] = useState(null);
  const [coverImageDeleted, setCoverImageDeleted] = useState(false); // Track if user wants to delete cover image
  const [categories, setCategories] = useState([createCategoryState({ name: 'Genel' })]);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!editingItem) {
      setFormData({
        name: '',
        urlTag: '',
        title: '',
        description: ''
      });
      setCoverImage(null);
      setCoverImagePreview(null);
      setCoverImageDeleted(false);
      setCategories([createCategoryState({ name: 'Genel' })]);
      return;
    }

    setFormData({
      name: editingItem.name || '',
      urlTag: editingItem.urlTag || '',
      title: editingItem.title || '',
      description: editingItem.description || ''
    });

    setCoverImage(null);
    setCoverImagePreview(getImageUrl(editingItem.coverImage) || null);
    setCoverImageDeleted(false);

    const categorySource = Array.isArray(editingItem.categories) && editingItem.categories.length > 0
      ? editingItem.categories
      : [
          {
            guid: null,
            name: 'Genel',
            description: '',
            images: editingItem.images || []
          }
        ];

    const allExistingImages = Array.isArray(editingItem.images) ? editingItem.images : [];
    const categoryImageGuids = new Set(
      categorySource
        .flatMap((category) => category.images || [])
        .map((image) => image?.guid)
        .filter(Boolean)
    );
    const uncategorizedExistingImages = allExistingImages.filter(
      (image) => image?.guid && !categoryImageGuids.has(image.guid)
    );
    const categoriesWithAllImages = categorySource.map((category, index) => ({
      ...category,
      images: index === 0
        ? [...(category.images || []), ...uncategorizedExistingImages]
        : (category.images || [])
    }));

    const mappedCategories = categoriesWithAllImages.map((category, index) =>
      createCategoryState({
        localId: category.guid || `existing-${index}-${createLocalId()}`,
        guid: category.guid || null,
        name: category.name || '',
        description: category.description || '',
        existingImages: (category.images || []).map((image) => ({
          ...image,
          previewUrl: getImageUrl(image.imageUrl)
        }))
      })
    );

    setCategories(mappedCategories.length > 0 ? mappedCategories : [createCategoryState({ name: 'Genel' })]);
  }, [editingItem]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => {
      const next = { ...prev, [name]: value };
      if (name === 'name' && !editingItem) {
        next.urlTag = generateSlug(value);
      }
      return next;
    });
  };

  const handleCoverUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    try {
      const base64 = await toBase64(file);
      console.log('Cover image uploaded, base64 length:', base64?.length);
      setCoverImage(base64);
      setCoverImagePreview(URL.createObjectURL(file));
      setCoverImageDeleted(false); // Reset delete flag when new image is uploaded
    } catch (error) {
      console.error('Error uploading cover image:', error);
      setError('Kapak fotoğrafı yüklenirken hata oluştu.');
    }
  };

  const clearCoverImage = () => {
    setCoverImage(null);
    setCoverImagePreview(null);
    setCoverImageDeleted(true); // Mark that user wants to delete the cover image
  };

  const handleAddCategory = () => {
    setCategories((prev) => [...prev, createCategoryState()]);
  };

  const handleRemoveCategory = (localId) => {
    if (categories.length === 1) {
      return;
    }

    setCategories((prev) => prev.filter((category) => category.localId !== localId));
  };

  const handleCategoryFieldChange = (localId, field, value) => {
    setCategories((prev) =>
      prev.map((category) =>
        category.localId === localId
          ? {
              ...category,
              [field]: value
            }
          : category
      )
    );
  };

  const handleCategoryUpload = async (localId, event) => {
    const files = Array.from(event.target.files || []);
    if (files.length === 0) return;

    const base64s = [];
    const previews = [];

    for (const file of files) {
      base64s.push(await toBase64(file));
      previews.push(URL.createObjectURL(file));
    }

    setCategories((prev) =>
      prev.map((category) =>
        category.localId === localId
          ? {
              ...category,
              newGalleryImages: [...category.newGalleryImages, ...base64s],
              newGalleryPreviews: [...category.newGalleryPreviews, ...previews]
            }
          : category
      )
    );

    event.target.value = null;
  };

  const handleRemoveExistingImage = (localId, guid) => {
    setCategories((prev) =>
      prev.map((category) =>
        category.localId === localId
          ? {
              ...category,
              existingImages: category.existingImages.filter((image) => image.guid !== guid)
            }
          : category
      )
    );
  };

  const handleRemoveNewImage = (localId, index) => {
    setCategories((prev) =>
      prev.map((category) =>
        category.localId === localId
          ? {
              ...category,
              newGalleryImages: category.newGalleryImages.filter((_, imageIndex) => imageIndex !== index),
              newGalleryPreviews: category.newGalleryPreviews.filter((_, imageIndex) => imageIndex !== index)
            }
          : category
      )
    );
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (!formData.name.trim()) {
      setError('Mahalle adı zorunludur.');
      return;
    }

    const preparedCategories = categories
      .map((category, index) => ({
        guid: category.guid,
        name: category.name.trim(),
        description: category.description.trim(),
        sortOrder: index + 1,
        existingImageGuids: category.existingImages.map((image) => image.guid),
        galleryImagesBase64: category.newGalleryImages
      }))
      .filter((category) =>
        category.name ||
        category.description ||
        category.existingImageGuids.length > 0 ||
        category.galleryImagesBase64.length > 0
      );

    if (preparedCategories.length === 0) {
      setError('En az bir kategori oluşturmalısınız.');
      return;
    }

    if (preparedCategories.some((category) => !category.name)) {
      setError('Her kategori için bir ad girmelisiniz.');
      return;
    }

    setSaving(true);
    try {
      const payload = {
        name: formData.name.trim(),
        urlTag: formData.urlTag.trim(),
        title: formData.title.trim(),
        description: formData.description.trim(),
        coverImageBase64: coverImage,
        deleteCoverImage: coverImageDeleted && !coverImage, // Only delete if user clicked X and didn't upload new image
        galleryImagesBase64: [],
        categories: preparedCategories
      };

      console.log('Submitting payload:', {
        ...payload,
        coverImageBase64: coverImage ? `[base64 data, length: ${coverImage.length}]` : null,
        deleteCoverImage: payload.deleteCoverImage,
        categories: payload.categories.map(c => ({
          ...c,
          galleryImagesBase64: c.galleryImagesBase64.map((_, i) => `[image ${i}]`)
        }))
      });

      let response;
      if (editingItem) {
        payload.guid = editingItem.guid;
        payload.existingImageGuids = [];
        response = await updateNeighborhoodDevelopment(payload);
      } else {
        response = await addNeighborhoodDevelopment(payload);
      }

      if (response?.isSuccess) {
        onSubmit();
      } else {
        throw new Error(response?.resultMessage || 'Kayıt işlemi başarısız oldu.');
      }
    } catch (err) {
      console.error(err);
      setError(err.message || 'Bir hata oluştu.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Form onSubmit={handleSubmit}>
      {error && <div className="alert alert-danger">{error}</div>}

      <Row>
        <Col md={6}>
          <Form.Group className="mb-3">
            <Form.Label>Mahalle Adı <span className="text-danger">*</span></Form.Label>
            <Form.Control
              type="text"
              name="name"
              value={formData.name}
              onChange={handleChange}
              placeholder="Örn: Barbaros Mahallesi"
              required
            />
          </Form.Group>
        </Col>
        <Col md={6}>
          <Form.Group className="mb-3">
            <Form.Label>URL Link Yapısı (Slug) <span className="text-danger">*</span></Form.Label>
            <Form.Control
              type="text"
              name="urlTag"
              value={formData.urlTag}
              onChange={handleChange}
              placeholder="Örn: barbaros"
              required
            />
          </Form.Group>
        </Col>
      </Row>

      <Form.Group className="mb-3">
        <Form.Label>İçerik Başlığı</Form.Label>
        <Form.Control
          type="text"
          name="title"
          value={formData.title}
          onChange={handleChange}
          placeholder="Detay sayfasında görünecek başlık"
        />
      </Form.Group>

      <Form.Group className="mb-3">
        <Form.Label>Açıklama / Yazı</Form.Label>
        <Form.Control
          as="textarea"
          rows={3}
          name="description"
          value={formData.description}
          onChange={handleChange}
          placeholder="Mahalle açıklaması"
        />
      </Form.Group>

      <Row className="mt-4">
        <Col md={12}>
          <h6 className="fw-bold mb-3 border-bottom pb-2">Kapak Fotoğrafı</h6>
          <div className="d-flex align-items-end gap-3 mb-3">
            <div
              style={{
                width: '200px',
                height: '150px',
                background: '#f8f9fa',
                border: '1px solid #dee2e6',
                borderRadius: '4px',
                overflow: 'hidden',
                position: 'relative'
              }}
            >
              {coverImagePreview ? (
                <Image src={coverImagePreview} alt="Kapak" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
              ) : (
                <div className="d-flex align-items-center justify-content-center h-100 text-muted">Görsel Yok</div>
              )}
              {coverImagePreview && (
                <Button
                  variant="danger"
                  size="sm"
                  className="position-absolute top-0 end-0 m-1"
                  style={{ padding: '0.1rem 0.3rem' }}
                  onClick={clearCoverImage}
                >
                  <FaTimes />
                </Button>
              )}
            </div>
            <Form.Group className="flex-grow-1">
              <Form.Control type="file" accept="image/*" onChange={handleCoverUpload} />
              <Form.Text className="text-muted">Liste ekranında ve detay sayfasının üst kısmında kullanılacak görsel.</Form.Text>
            </Form.Group>
          </div>
        </Col>
      </Row>

      <Row className="mt-4">
        <Col md={12}>
          <div className="d-flex justify-content-between align-items-center mb-3 border-bottom pb-2">
            <h6 className="fw-bold mb-0">Kategori Bazlı Fotoğraflar</h6>
            <Button variant="outline-primary" size="sm" onClick={handleAddCategory}>
              <FaPlus className="me-2" />
              Kategori Ekle
            </Button>
          </div>

          {categories.map((category, index) => (
            <Card key={category.localId} className="mb-4 shadow-sm">
              <Card.Body>
                <div className="d-flex justify-content-between align-items-center mb-3">
                  <h6 className="mb-0">Kategori {index + 1}</h6>
                  <Button
                    variant="outline-danger"
                    size="sm"
                    onClick={() => handleRemoveCategory(category.localId)}
                    disabled={categories.length === 1}
                  >
                    <FaTrash className="me-2" />
                    Kaldır
                  </Button>
                </div>

                <Row>
                  <Col md={5}>
                    <Form.Group className="mb-3">
                      <Form.Label>Kategori Adı <span className="text-danger">*</span></Form.Label>
                      <Form.Control
                        type="text"
                        value={category.name}
                        onChange={(e) => handleCategoryFieldChange(category.localId, 'name', e.target.value)}
                        placeholder="Örn: 312. Sokak"
                      />
                    </Form.Group>
                  </Col>
                  <Col md={7}>
                    <Form.Group className="mb-3">
                      <Form.Label>Kategori Açıklaması</Form.Label>
                      <Form.Control
                        type="text"
                        value={category.description}
                        onChange={(e) => handleCategoryFieldChange(category.localId, 'description', e.target.value)}
                        placeholder="Opsiyonel kısa açıklama"
                      />
                    </Form.Group>
                  </Col>
                </Row>

                <Form.Group className="mb-3">
                  <Form.Label>Bu Kategoriye Fotoğraf Ekle</Form.Label>
                  <Form.Control
                    type="file"
                    accept="image/*"
                    multiple
                    onChange={(e) => handleCategoryUpload(category.localId, e)}
                  />
                  <Form.Text className="text-muted">
                    Bu kategoriye ait görseller public tarafta aynı başlık altında listelenir.
                  </Form.Text>
                </Form.Group>

                {category.existingImages.length > 0 && (
                  <div className="mb-4">
                    <div className="text-muted small mb-2">Mevcut Fotoğraflar</div>
                    <div className="d-flex flex-wrap gap-2">
                      {category.existingImages.map((image) => (
                        <div
                          key={image.guid}
                          className="position-relative border rounded p-1"
                          style={{ width: '100px', height: '100px' }}
                        >
                          <Image src={image.previewUrl} style={{ width: '100%', height: '100%', objectFit: 'cover' }} rounded />
                          <Button
                            variant="danger"
                            size="sm"
                            className="position-absolute top-0 end-0 m-1"
                            onClick={() => handleRemoveExistingImage(category.localId, image.guid)}
                            title="Sil"
                            style={{ padding: '0 4px' }}
                          >
                            &times;
                          </Button>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {category.newGalleryPreviews.length > 0 && (
                  <div className="mb-3">
                    <div className="text-muted small mb-2">Yeni Eklenecek Fotoğraflar</div>
                    <div className="d-flex flex-wrap gap-2">
                      {category.newGalleryPreviews.map((preview, imageIndex) => (
                        <div
                          key={`${category.localId}-${imageIndex}`}
                          className="position-relative border border-primary rounded p-1"
                          style={{ width: '100px', height: '100px' }}
                        >
                          <Image src={preview} style={{ width: '100%', height: '100%', objectFit: 'cover' }} rounded />
                          <Button
                            variant="danger"
                            size="sm"
                            className="position-absolute top-0 end-0 m-1"
                            onClick={() => handleRemoveNewImage(category.localId, imageIndex)}
                            title="Sil"
                            style={{ padding: '0 4px' }}
                          >
                            &times;
                          </Button>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </Card.Body>
            </Card>
          ))}
        </Col>
      </Row>

      <div className="d-flex justify-content-end gap-2 mt-4 pt-3 border-top">
        <Button variant="secondary" onClick={onCancel} disabled={saving}>
          İptal
        </Button>
        <Button variant="primary" type="submit" disabled={saving}>
          {saving ? <Spinner animation="border" size="sm" /> : <><FaSave className="me-2" /> Kaydet</>}
        </Button>
      </div>
    </Form>
  );
};

export default NeighborhoodForm;
